import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Icon, Message, Modal, Popup } from 'semantic-ui-react';
import { API } from '../../helpers';
import './MemberCreditV108.css';

export const creditRequestKeyV108 = () =>
  window.crypto?.randomUUID?.() ||
  `credit-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const validCreditAmountV108 = (amount) =>
  /^\d+(\.\d{1,9})?$/.test(amount) && /[1-9]/.test(amount);

export const creditErrorV108 = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const MemberCreditV108 = ({ member, accountId, admin, virtual, onSaved }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState('credit');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [rows, setRows] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [retry, setRetry] = useState(0);
  const busy = useRef(false);
  const request = useRef(null);
  const endpoint = admin
    ? `/api/billing/v8/admin/organizations/${accountId}/members/${member.user.id}/credit`
    : `/api/billing/v8/organization/members/${member.user.id}/credit`;
  const wallet = !virtual && member.funded_wallet_v109;
  const unlimited = !virtual && !wallet && member.cycle_limit_usd == null;
  const label = t('account_credit.adjust');

  useEffect(() => {
    if (!open) return;
    let current = true;
    setHistoryError('');
    setRows(null);
    API.get(endpoint)
      .then((response) => {
        if (!response?.data?.success)
          throw new Error(
            response?.data?.message || t('account_credit.load_error')
          );
        if (current) setRows(response.data.data);
      })
      .catch(
        (e) =>
          current &&
          setHistoryError(creditErrorV108(e, t('account_credit.load_error')))
      );
    return () => {
      current = false;
    };
  }, [endpoint, open, retry, t]);

  const start = () => {
    setDirection('credit');
    setAmount('');
    setNote('');
    setError('');
    setReceipt(null);
    request.current = null;
    setOpen(true);
  };
  const submit = async () => {
    if (busy.current || receipt || unlimited) return;
    if (!validCreditAmountV108(amount) || !note.trim()) {
      setError(t('account_credit.invalid'));
      return;
    }
    const payload = { direction, amount_usd: amount, description: note.trim() };
    const fingerprint = JSON.stringify(payload);
    if (request.current?.fingerprint !== fingerprint)
      request.current = { fingerprint, key: creditRequestKeyV108() };
    busy.current = true;
    setSaving(true);
    setError('');
    try {
      const response = await API.post(endpoint, {
        ...payload,
        idempotency_key: request.current.key,
      });
      if (!response?.data?.success)
        throw new Error(response?.data?.message || t('account_credit.failed'));
      setReceipt(response.data.data);
      setRetry((value) => value + 1);
    } catch (e) {
      setError(creditErrorV108(e, t('account_credit.failed')));
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };
  const close = () => {
    if (busy.current) return;
    setOpen(false);
    if (receipt) onSaved?.();
  };
  return (
    <>
      <Popup
        content={label}
        trigger={
          <Button
            icon
            size='small'
            className='member-credit-trigger-v108'
            aria-label={`${label}: ${member.user.username}`}
            onClick={start}
          >
            <Icon name='dollar sign' />
          </Button>
        }
      />
      <Modal
        open={open}
        onClose={close}
        closeIcon={!saving}
        closeOnEscape={!saving}
        closeOnDimmerClick={!saving}
        size='small'
        className='member-credit-modal-v108'
      >
        <Modal.Header>
          {label} · {member.user.username}
        </Modal.Header>
        <Modal.Content>
          <dl className='account-credit-summary-v108'>
            <div>
              <dt>
                {t(
                  virtual || wallet
                    ? 'account_credit.balance'
                    : 'account_credit.cycle_limit'
                )}
              </dt>
              <dd>
                {receipt
                  ? receipt.after_usd == null
                    ? t('organization.unlimited')
                    : `$${receipt.after_usd}`
                  : unlimited
                  ? t('organization.unlimited')
                  : `$${
                      virtual
                        ? member.quota_usd
                        : wallet
                        ? member.usage_summary?.wallet_balance_usd ??
                          member.usage_summary?.remaining_usd ??
                          '-'
                        : member.cycle_limit_usd
                    }`}
              </dd>
            </div>
            <div>
              <dt>{t('account_credit.scope')}</dt>
              <dd>
                {t(
                  virtual || wallet
                    ? 'account_credit.personal'
                    : 'account_credit.member_budget'
                )}
              </dd>
            </div>
          </dl>
          {unlimited && (
            <Message visible warning content={t('account_credit.unlimited')} />
          )}
          {receipt ? (
            <Message
              visible
              positive
              role='status'
              content={`${t('account_credit.success')} ${
                receipt.direction === 'debit' ? '-' : '+'
              }$${receipt.amount_usd}`}
            />
          ) : (
            <Form onSubmit={submit}>
              <Form.Field>
                <label>{t('account_credit.operation')}</label>
                <Button.Group className='account-credit-mode-v108'>
                  {['credit', 'debit'].map((value) => (
                    <Button
                      key={value}
                      type='button'
                      active={direction === value}
                      aria-pressed={direction === value}
                      disabled={saving}
                      onClick={() => setDirection(value)}
                    >
                      <Icon name={value === 'credit' ? 'plus' : 'minus'} />
                      {t(`account_credit.${value}`)}
                    </Button>
                  ))}
                </Button.Group>
              </Form.Field>
              <Form.Input
                id={`member-credit-amount-${member.user.id}`}
                label={t('account_credit.amount')}
                inputMode='decimal'
                autoComplete='off'
                value={amount}
                maxLength={32}
                disabled={saving || unlimited}
                onChange={(_, data) => setAmount(data.value)}
              />
              <Form.Input
                id={`member-credit-note-${member.user.id}`}
                label={t('account_credit.note')}
                value={note}
                maxLength={200}
                disabled={saving || unlimited}
                onChange={(_, data) => setNote(data.value)}
              />
              {error && (
                <Message visible negative role='alert' content={error} />
              )}
              <Button
                type='submit'
                primary
                loading={saving}
                disabled={
                  saving ||
                  unlimited ||
                  !validCreditAmountV108(amount) ||
                  !note.trim()
                }
              >
                <Icon name='check' />
                {t(`account_credit.confirm_${direction}`)}
              </Button>
            </Form>
          )}
          <section
            className='account-credit-history-v108'
            aria-label={t('account_credit.history')}
          >
            <h3>{t('account_credit.history')}</h3>
            {historyError ? (
              <Message visible negative role='alert'>
                {historyError}
                <Button
                  type='button'
                  icon='refresh'
                  aria-label={t('account_credit.retry')}
                  onClick={() => setRetry((value) => value + 1)}
                />
              </Message>
            ) : rows == null ? (
              <p>{t('account_credit.loading')}</p>
            ) : rows.length === 0 ? (
              <p>{t('account_credit.empty')}</p>
            ) : (
              <ul>
                {rows.map((row) => (
                  <li key={row.id}>
                    <span>
                      {row.direction === 'debit' ? '-' : '+'}${row.amount_usd}
                    </span>
                    <span>{row.description}</span>
                    <time
                      dateTime={new Date(row.created_at * 1000).toISOString()}
                    >
                      {new Date(row.created_at * 1000).toLocaleString()}
                    </time>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </Modal.Content>
        <Modal.Actions>
          <Button disabled={saving} onClick={close}>
            {t('account_credit.close')}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};
export default MemberCreditV108;
